import { WinPopUp, WinPopUpParams } from './WinPopUp';
import { createButton, createSprite, translate } from '../../utils/GameEngine';
import { TextStyles } from '../../utils/TextStyles';
import Sprite = PIXI.Sprite;
import Text = PIXI.Text;
import Graphics = PIXI.Graphics;
import TextStyle = PIXI.TextStyle;
import Container = PIXI.Container;
import { ProgressBar } from './ProgressBar';
import { TextIncrement } from '../../UI/elements/TextIncrement';
import { getRandomFromInterval } from '../../utils/MathUtils';
import { ShareButton } from '../../UI/elements/buttons/ShareButton';
import { Loader } from '../../Loader';
import { SceneManager } from '../../core/managers/SceneManager';
import { GameAnalytics } from '../../analytics/GameAnalytics';
import { AnalyticsEventNames } from '../../analytics/AnalyticsEventNames';
import { PlayWithFriendButton } from '../../UI/elements/buttons/PlayWithFriendButton';
import { CoreGameplay } from '../../core/gameplay/CoreGameplay';
import { InviteTemplate, SocialManager } from '../../core/managers/SocialManager';
import { FBAdManager } from '../../core/managers/abstract/FBAdManager';
import { CoreController } from '../../core/CoreController';

export interface ChapterWinPopupParams extends WinPopUpParams {
    levelIndex: number;
    levelScore: number;
}

export class ChapterWinPopup extends WinPopUp {
    private shareButton: ShareButton;
    private base64toShare: any;
    private inviteFriendButton: PlayWithFriendButton;

    protected drawLayout(params?: any): void {
        const isFB: boolean = CoreController.getInstance().isFB();
        const percentage: number = Math.floor((this.params as ChapterWinPopupParams).levelIndex / 128 * 100) + getRandomFromInterval(0, 2);
        const canShare: boolean =
            (this.params as ChapterWinPopupParams).levelIndex > 3 && (this.params as ChapterWinPopupParams).levelIndex % 3 === 0 && isFB;
        super.drawLayout(params);
        if (!canShare) {
            this.addTitle();
            this.addProgress(percentage);
            this.addScores();
            this.addButtons();
        } else {
            this.addShare();
        }
    }

    private addTitle(): void {
        this.background.cacheAsBitmap = false;
        const chapterContainer: Container = new Container();
        this.background.addChild(chapterContainer);
        const donut: Sprite = createSprite('win_mascot', chapterContainer);
        donut.scale.set(0.7, 0.7);
        donut.x = -this.background.width / 2 + donut.width / 2 - 10;
        chapterContainer.scale.set(0.7, 0.7);
        chapterContainer.pivot.set(chapterContainer.width / 2, chapterContainer.height / 2);
        chapterContainer.position.set(this.bgSprite.width / 2 - 30, 100);

        const winText: Text = new Text(
            translate('You Won', true) + '\nLevel ' + (this.params as ChapterWinPopupParams).levelIndex,
            TextStyles.chapterWinTextLeft
        );
        winText.anchor.set(0, 0.5);
        winText.x = donut.x + 30 + donut.width;
        winText.y = donut.y + donut.height / 2;
        chapterContainer.addChild(winText);
    }

    private addProgress(percentage: number): void {
        const style: TextStyle = TextStyles.smallTextWhite.clone();
        style.wordWrap = false;

        const text: TextIncrement = new TextIncrement(
            1,
            translate('You beat') + ' ',
            translate('% of players') + '!',
            style,
            this,
            false,
            percentage,
            true
        );
        text.anchor.set(0.5, 0.5);
        text.position.set(this.bgSprite.width / 2 + 25, 220);
        this.increments = [];
        this.increments.push(text);

        const progressBar: ProgressBar = new ProgressBar(percentage);
        progressBar.scale.set(1.5, 1.5);
        progressBar.position.set((this.bgSprite.width - progressBar.width) / 2 + 25, text.y + 40);
        this.addChild(progressBar);
    }

    private addScores(): void {
        const textY: number = this.bgSprite.height - 60;

        const chapterScore: TextIncrement = new TextIncrement(
            1,
            translate('level score') + ': ',
            '',
            TextStyles.winPopupText,
            this,
            false,
            (this.params as ChapterWinPopupParams).levelScore,
            true
        );
        chapterScore.anchor.set(1, 0.5);
        chapterScore.position.set(this.bgSprite.width / 2 - 10, textY);

        this.increments.push(chapterScore);
    }

    private async addButtons(): Promise<void> {
        if (CoreController.getInstance().isFB()) {
            const inviteTemplate: InviteTemplate = await SocialManager.getInstance().getInviteTemplate();

            this.inviteFriendButton = new PlayWithFriendButton((): void => {
                GameAnalytics.getInstance().logDesign(AnalyticsEventNames.invite_attempt);
                FBInstant.inviteAsync(inviteTemplate).then((result: any): void => {
                    this.core.adShown();
                    this.returnFunction = (this.core as CoreGameplay).getOnContinueFunction();
                    this.hide();
                });
            });
            this.addChild(this.inviteFriendButton);
            this.inviteFriendButton.position.set(this.background.width / 2, this.buttonY);
        } else {
            this.continueButton = createButton(
                'green_btn_large@2x',
                this,
                (): void => {
                    this.hide();
                },
                translate('Continue', true),
                true,
                true,
                1
            );
            this.continueButton.position.set(this.background.width / 2, this.buttonY);
        }
    }

    private addShare(): void {
        this.background.visible = false;
        this.closeButton.visible = false;
        let imageIndex: number = getRandomFromInterval(1, 7);
        if (imageIndex === 0) {
            imageIndex = 7;
        }
        const bgTextureName: string = 'donut_frame_0' + imageIndex;
        const container: Container = new PIXI.Container();
        Loader.getInstance().loadShareAsset(bgTextureName, (resourceName: string): void => {
            this.shareButton = new ShareButton(this.share.bind(this));
            this.addChild(this.shareButton);
            this.shareButton.interactive = true;
            const myPhoto: Sprite = PIXI.Sprite.from(FBInstant.player.getPhoto());
            setTimeout((): void => {
                const background: Graphics = new Graphics();
                background.beginFill(0xFFFFFF);
                background.drawRect(
                    0,
                    0,
                    420 + 10,
                    500
                );
                background.endFill();
                this.addChild(background);
                container.addChild(myPhoto);
                const image: Sprite = createSprite(resourceName, container);
                image.width = myPhoto.width = 1024;
                image.height = myPhoto.height = 1024;
                const extract: Extract<any, any> = new PIXI.Extract(SceneManager.getInstance()._app.renderer);
                this.base64toShare = extract.base64(container);
                this.addChild(container);
                container.width = container.height = 420;
                container.pivot.set(container.width / 2, container.height / 2);
                container.position.set(-container.width / 4, -108);
                background.x = -196;
                background.y = -200;

                this.closeButton.visible = true;
                this.closeButton.position.set(
                    container.width / 2 + this.closeButton.width / 2,
                    -container.height / 2 + this.closeButton.height / 2
                );
                this.addChild(this.closeButton);
                this.addChild(this.shareButton);
                this.shareButton.position.set(
                    14,
                    262
                );
            }, 300);
        });
    }

    private share(): void {
        const chapterIndex: number = (this.params as ChapterWinPopupParams).levelIndex;
        this.shareButton.interactive = false;
        FBAdManager.getInstance().hideBannerAd();

        FBInstant.shareAsync({
            intent: 'SHARE',
            image: this.base64toShare,
            text: 'I just finished Chapter' + ' ' + chapterIndex + '!',
        }).then((): void => {
            (this.params as WinPopUpParams).core.adShown();
            this.returnFunction = (this.core as CoreGameplay).getOnContinueFunction();
            GameAnalytics.getInstance().logDesign(AnalyticsEventNames.share_end_chapter, true);
            this.hide(true);
        });
    }
}